// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { isEmpty } from 'lodash';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { PaymentTerm } from '@makemydeal/dr-platform-types';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { actions as offerReduxActionTypes, paymentActionTypes } from '@makemydeal/dr-offer-redux';

// actions
import * as offerActionCreators from '../actions/offerActionCreators';

// action types
import { APPLY_LEASE_CONTRACT_FIELDS } from '../actionTypes/leaseContractingFieldsActionTypes';
import { UPDATE_CURRENT_CALCULATION_ENDPOINT, RESOLVE_PENDING_DEALER_REVIEW_PAYMENT } from '../actionTypes/offerActionTypes';
import { PRODUCT_CHANGES_PAYMENT_CALL } from '../actionTypes/vehicleProtectionActionTypes';

// selectors / utils
import * as offerReduxSelectors from '../selectors/offerRedux';
import { findPendingOperation } from './paymentSequenceRegistry';
// Side effect only: installing the guard is what populates the fail-open registry, so the two cannot come
// apart in a host that composes this middleware without the rest of the chain.
import './paymentSequencePendingOperations';
import { isPqOverrideRecoveryOutcome, releasePqOverrideRecoveryHold } from './__common__/pqOverrideRecovery';
import { isMainPaymentTrigger } from '../utils/paymentActionUtils';
import { createStoreScopeState, resolveScopeId } from '../utils/storeScopeState';
import { buildUsabilityContext } from '../utils/usabilityContext';
import type { UsabilityContext } from '../utils/usabilityTelemetry';
import {
    trackPaymentSequenceDiscarded,
    trackPaymentSequencePassThrough,
    trackTermIncentiveRestored
} from '../utils/usabilityTelemetry';

/**
 * Global payment sequence guard — "latest DISPATCHED wins" (Rally US2069413 / F303836).
 *
 * Every MV payment call funnels through one place: an action carrying `meta.middleware.payment: true`
 * reaches offer-redux's `paymentMiddleware`, which dispatches `PAYMENT_REQUESTED` then `calculatePayments`;
 * every result exits as `PAYMENT_RECEIVED` / `PAYMENT_FAILURE` / `PAYMENT_ABORTED` /
 * `PAYMENT_FETCH_COMPLETED`, wrapped by `withMeta` so the triggering action sits at `meta.originalAction`.
 *
 * This middleware stamps a monotonic sequence on every payment-triggering action on the way down, and on
 * the way back refuses to forward a response whose originating sequence is lower than the latest sequence
 * *dispatched* in the same lane. A stale response in that set becomes structurally unable to reach the
 * reducer layer, so new field-edit components inherit the fix with no per-component work.
 *
 * ONE HALF OF THE FAILURE PATH IS OUT OF REACH, and it is a real gap rather than a simplification.
 * `calculatePayments` dispatches `paymentFailed(action)` just BEFORE `PAYMENT_FAILURE`, and that action is a
 * bare `{ type }` from `paymentFailureMappings` — `ZIP_CODE_PAYMENT_FAILED`, `OFFER_PRICE_PAYMENT_FAILED`, …
 * — carrying no meta at all, so nothing identifies which request it belongs to and this middleware cannot
 * attribute it to a lane. A superseded zip change therefore still reaches `paymentReducer`, which sets
 * `error: true` and clears the spinner, even after a newer edit has already rendered. Closing it means
 * routing `paymentFailed` through `withMeta` in offer-redux — a change to the package Shopper Platform pins,
 * deliberately out of scope here.
 *
 * "Latest dispatched", not "latest resolved": the moment a newer call goes out, every older in-flight
 * call is already obsolete. Comparing against the highest *resolved* sequence (what
 * `paymentRaceTelemetryMiddleware` does — correct for *detecting* out-of-order landing) is too lax to
 * prevent it.
 *
 * "Dispatched" means A REQUEST WENT OUT, which is why the bar is raised on `PAYMENT_REQUESTED` rather than at
 * stamp time. Stamping happens upstream of the middleware that decides whether to issue a request at all, so
 * a stamped action can produce no request and therefore no response, ever — dash `paymentMiddleware` returns
 * early on an invalid deal and again before `BOOTSTRAP_SUCCESS` has configured it. Raising the bar at stamp
 * time let such a phantom supersede a genuinely in-flight call: its good response was discarded, nothing
 * replaced it, and the deal kept the pre-edit payment with the spinner cleared and no error.
 *
 * ORDERING IS LOAD-BEARING. This must sit upstream of every middleware that reacts to `PAYMENT_RECEIVED`
 * after `next()` returns, because refusing to forward is the entire mechanism. A middleware registered
 * *before* this one still receives the response from its own `next(action)` call and acts on it, so it is
 * registered in `getInitialMiddleware` (near the top of the chain), NOT alongside the payment middlewares.
 * See the wiring comment in `apps/accelerate/dash/src/shells/main/store/initialMiddleware.ts`.
 *
 * See `README.md` in this directory for how to exempt a new workflow.
 */

/** Meta key carrying the monotonic sequence. A plain number, so actions stay serializable. */
export const PAYMENT_SEQ_META = 'paymentSeq';

/**
 * Meta key carrying the lane the action was stamped into.
 *
 * The lane is resolved once, at stamp time, and carried on the action rather than recomputed at
 * resolution time. Recomputing would read `getCurrentOfferType` from whatever state exists when the
 * response lands, so a deal-type switch mid-flight would send a response to a different lane than the one
 * that counted its dispatch — the comparison would then be against an unrelated lane's counter.
 */
export const PAYMENT_SEQ_LANE_META = 'paymentSeqLane';

/**
 * Meta key carrying the scope id (`__realStore` + `__scopeId`, see `storeScopeState.ts`) the action was
 * stamped into.
 *
 * Same reasoning as `PAYMENT_SEQ_LANE_META`, one level up: `createStoreScopeState` derives `__scopeId` from
 * whatever store dispatched the current action, which is safe only because the scoped-store framework
 * (`scopedStoreManager.createScopedDispatch`) re-tags every action a scoped thunk dispatches with the scope
 * id closed over at request time, rather than one read from live UI state when the response happens to land.
 * That is an invariant of a package this middleware does not own. Pinning the resolved id here — and having
 * resolution look it up directly instead of re-deriving it from whatever store is live when the response
 * arrives — means a future change to that invariant fails a stamped response into the wrong lane loudly
 * (compares against a fresh, empty counter and passes stale data through) rather than depending silently on
 * a contract this file cannot see broken.
 */
export const PAYMENT_SEQ_SCOPE_META = 'paymentSeqScope';

/**
 * Marks the synthetic `PAYMENT_ABORTED` this middleware dispatches after a discard.
 *
 * Without it that action re-enters the chain, matches every discard condition again (its
 * `meta.originalAction` is the same superseded trigger), and dispatches another one — an infinite loop.
 */
export const PAYMENT_SEQ_DISCARDED_META = 'paymentSeqDiscarded';

/**
 * BUCKET B — completion-signal triggers. Never discarded.
 *
 * A middleware treats these triggers' `PAYMENT_RECEIVED` as "my operation finished" and dispatches the
 * action that closes a slideout or ends a spinner. Discarding one hangs the operation with no recovery,
 * so the response is forwarded untouched even when superseded.
 *
 * "A middleware" is shorthand: a REDUCER that keys off the trigger counts too, and is easier to miss
 * because there is no `case PAYMENT_RECEIVED:` in a middleware to grep for. A discard never reaches the
 * reducer layer either, so a rollback or failure flag written there is stranded just as thoroughly.
 *
 * Also here: chain-establishing triggers, whose response is the base every later calculation builds on
 * (deal-type switch is the motivating case — it alone drives grid reset/init, the `leaseEndpointMiddleware`
 * PQ trigger and PQ-first fallback, and the FnI calc-endpoint switch). Exempting one preserves today's
 * behavior but does NOT fix the race for it; the base response can still land late and overwrite a newer
 * edit. Fixing that needs a request barrier, which is a user-visible behavior change and out of scope here.
 *
 * EXEMPT MEANS "NEVER DISCARDED", NOT "NEVER SUPERSEDES". An exempt trigger still raises the lane's bar, so an
 * older field edit still loses to it. Conflating the two made every entry on this list a hole in BOTH
 * directions: apply a lender program right after a cash-down edit and the edit's response, arriving second,
 * was forwarded on top of the program's payments. Nothing about a workflow needing its own response protects
 * an unrelated older response, so the two concerns are kept apart — `notDiscardableReason` decides what may be
 * discarded, `isMainPaymentTrigger` alone decides what raises the bar.
 *
 * ONE ENTRY GROUP IS CONDITIONAL. The rate and residual overrides below are exempt only until an ordinary edit
 * overtakes them, and then only for a failure or abort (DE784527, `RATE_OVERRIDE_TRIGGERS`). A reactor keyed on
 * one of them must cope with its `PAYMENT_RECEIVED` being discarded, as the PQ override recovery's release does.
 *
 * GUARD BY DEFAULT, EXEMPT BY EXCEPTION — and being guarded costs nothing. A new middleware that reacts to
 * `PAYMENT_RECEIVED` is auto-enrolled: it sits downstream of this middleware, so the guard already applies
 * with no list to edit and no registration step. This list is purely the opt-OUT, for the minority of
 * triggers whose response must survive supersession.
 */
export const PAYMENT_SEQUENCE_EXEMPT_TRIGGERS: ReadonlySet<string> = new Set<string>([
    // Slideout / operation completion — swallowing hangs the workflow.
    offerReduxActionTypes.APPLY_DUE_AT_SIGNING, // dueAtSigningMiddleware → applyDueAtSigningSuccess()
    offerReduxActionTypes.APPLY_ROLL_TO_OPTIONS, // rollToOptionsMiddleware → roll-to success/error
    APPLY_LEASE_CONTRACT_FIELDS, // leaseContractingFieldsMiddleware → applyLeaseContractingFieldsSuccess
    offerReduxActionTypes.APPLY_DEALER_DISCOUNTS, // dealerDiscountsMiddleware → APPLY_DEALER_DISCOUNTS_SUCCESS
    offerReduxActionTypes.SWITCH_VEHICLE, // changeVehicleMiddleware → finishVehicleChange() + restoreCalcEndpoint()
    offerReduxActionTypes.APPLIED_PROGRAM, // programsMiddleware → clearPendingApplyProgram(), incentives-from-payment
    offerReduxActionTypes.UPDATED_CREDIT_DECISION_ENABLED, // paymentMiddleware → applyDealerOverrides() chain

    // State fix-up / rollback whose handler lives in a REDUCER, not a middleware. Same hazard, less
    // visible: a discard never reaches the reducer layer either, so the fix-up simply never happens and
    // nothing at runtime says so.
    PRODUCT_CHANGES_PAYMENT_CALL, // vehicleProtectionReducer → isPaymentFailed / lastFailureAction
    UPDATE_CURRENT_CALCULATION_ENDPOINT, // paymentMiddleware PAYMENT_RECEIVED → CLEAR_OVERRIDES
    RESOLVE_PENDING_DEALER_REVIEW_PAYMENT, // pendingDealerReviewMiddleware PAYMENT_RECEIVED/FAILURE → resolution outcome
    // DE766084: paymentReducer PAYMENT_FAILURE → shopperUpdateFailed, which LenderChangeAlert reads to
    // auto-retry. A zip change is now program-invalidating, so the refetch's endpoint switch raises the bar
    // while this call is still in flight; discarded, the retry never fires and the user keeps the error.
    offerReduxActionTypes.UPDATED_SHOPPER_CONTACT,

    // DE761542: the response carries the rate or residual the dealer just typed. Such an edit fans out one payment per
    // cash-down row and then triggers its own UPQ/PQ endpoint switch, which raises the bar while those rows are
    // still in flight - discarding them strands the edit, so the deal keeps and saves the pre-edit value with no
    // error shown. Exempt means never discarded, not never supersedes, so these still beat older edits. DE784527:
    // exempt only until an ordinary edit overtakes them - see RATE_OVERRIDE_TRIGGERS.
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE,

    // Chain-establishing — their response is the base later calculations and the grid build on.
    offerReduxActionTypes.UPDATED_OFFER_TYPE,
    offerReduxActionTypes.FETCH_OFFER_TYPES_DATA,
    offerReduxActionTypes.APPLY_DEALER_PAYMENT_OVERRIDES,
    offerReduxActionTypes.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE
]);

/**
 * Notable triggers that are guarded, i.e. absent from the list above, and are meant to stay that way:
 *
 * - `UPDATED_PAYMENT_FREQUENCY` — `paymentFrequencyMiddleware` emits telemetry only, and suppressing a
 *   stale "Changed" event is desirable.
 * - `LOAD_PAYMENT_GRID_LENDER_PROGRAMS`, `LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM`,
 *   `UPDATE_FLEX_BUY_TERMS_GRID` — forwarded by the grid check in `isGuardable` rather than by exemption,
 *   which is what keeps `handleGridActions`' population chain intact.
 */

/** Resolving action types the guard inspects. */
const RESOLVING_TYPES: ReadonlySet<string> = new Set<string>([
    paymentActionTypes.PAYMENT_RECEIVED,
    paymentActionTypes.PAYMENT_FAILURE,
    paymentActionTypes.PAYMENT_ABORTED,
    paymentActionTypes.PAYMENT_FETCH_COMPLETED
]);

/**
 * `PAYMENT_FETCH_COMPLETED` fires alongside a terminal action for the same call, so only the terminal
 * types decrement the outstanding count.
 */
const TERMINAL_TYPES: ReadonlySet<string> = new Set<string>([
    paymentActionTypes.PAYMENT_RECEIVED,
    paymentActionTypes.PAYMENT_FAILURE,
    paymentActionTypes.PAYMENT_ABORTED
]);

/**
 * `inFlight` holds the sequences of payments in this lane that have a CONFIRMED request and no terminal
 * response yet. It exists for exactly one decision: whether a discard leaves nobody to clear the spinner.
 *
 * IT IS POPULATED ON `PAYMENT_REQUESTED`, NOT AT STAMP TIME, and both halves of that matter:
 *
 * - Stamping happens upstream of the middleware that decides whether a request goes out at all. Dash
 *   `paymentMiddleware` returns early when the deal is invalid, and again before BOOTSTRAP_SUCCESS has
 *   configured it, so a stamped action can produce no request and therefore no terminal action, ever.
 *   Counting at stamp time would pin the lane to a sequence that can never resolve, permanently
 *   suppressing the recovery dispatch below — a stuck spinner and a stuck recalculating dialog.
 *   `latestDispatched` is raised from this same signal, for the same reason: see the middleware docstring.
 * - Every payment is counted here, guardable or not. Exempt triggers and concurrent grid rows also set
 *   `isCalculating` and also clear it when they resolve, so a ledger that skipped them would report
 *   "nothing outstanding" while a program apply or a grid row was still running, and the recovery dispatch
 *   below would clear the spinner out from under it.
 *
 * KEYED BY SEQUENCE, not a count: `delete` is idempotent, so a resolving action that arrives twice for one
 * request cannot drop the lane below its true depth, and a delete is keyed by the sequence it belongs to where
 * a decrement is keyed by nothing.
 *
 * Each entry carries whether its response is DISCARDABLE, which is what makes the sweep safe. When the
 * latest-dispatched sequence resolves, older discardable entries are dropped: their responses can only be
 * discarded from here on, so they are no longer a reason to withhold the recovery — and dropping them is what
 * stops a skipped resolution (a request confirmed while the toggle was on whose response landed while it was
 * off) from leaking an entry forever. Older AND DISCARDABLE only. An exempt trigger or a grid row in the same
 * lane is never discarded, so it is genuinely still outstanding however old it is; sweeping it would report
 * "nothing outstanding" while a program apply was still running and let the recovery below clear the spinner
 * out from under it — the same failure the ledger counts them to prevent.
 *
 * Discardability is recorded from the TRIGGER TYPE, which is fixed for the life of the request, with one entry
 * judged at sweep time instead: a rate or residual override, discardable once an ordinary edit has overtaken it
 * (DE784527) - which is only known after it was confirmed. The
 * `missingRequiredPSFields` half of `notDiscardableReason` deliberately does not participate: it is written
 * during payload build, i.e. after this entry is made, and an entry swept on an optimistic `true` only ever
 * costs a redundant recovery for a response that is about to be forwarded anyway.
 */
type LaneState = {
    latestDispatched: number;
    /**
     * The newest confirmed MAIN payment whose trigger is not exempt - an ordinary edit. What lifts a rate or
     * residual override's exemption (DE784527) is that such an edit went out after it, whatever was dispatched
     * after that: deciding from `latestDispatchedAction` alone let an exempt call made later (an endpoint switch)
     * hide the newer edit and put the stale response back. It only ever rises, so an entry the sweep drops on it
     * can never be judged un-discardable again.
     */
    latestOrdinaryDispatched: number;
    /**
     * The trigger that raised `latestDispatched`, kept by reference so its `meta` can be re-read at resolution
     * time — see `carriesDealerOverrideFollowUp`, which needs to know whether the superseding call inherited
     * the same state-derived follow-up.
     */
    latestDispatchedAction?: AnyFSA;
    /**
     * sequence → entry carrying sweep eligibility and whether it counts toward the Redux in-flight counter.
     * `isRateOverride` marks the one exempt entry whose eligibility is decided at sweep time (DE784527).
     */
    inFlight: Map<number, { discardable: boolean; isMainPaymentTrigger: boolean; isRateOverride: boolean }>;
};
type ScopeSeqState = {
    counter: number;
    lanes: Map<string, LaneState>;
    termIncentiveSeq: Map<string, number>;
    /** DE785100: per term id, the highest seq whose PAYMENT_RECEIVED was forwarded. */
    appliedTermSeq: Map<string, number>;
};

const getScopeState = createStoreScopeState<ScopeSeqState>(() => ({
    counter: 0,
    lanes: new Map(),
    termIncentiveSeq: new Map(),
    appliedTermSeq: new Map()
}));

const getLane = (scopeState: ScopeSeqState, laneKey: string): LaneState => {
    let lane = scopeState.lanes.get(laneKey);
    if (!lane) {
        lane = { latestDispatched: 0, latestOrdinaryDispatched: 0, inFlight: new Map() };
        scopeState.lanes.set(laneKey, lane);
    }
    return lane;
};

/** The stamp this middleware wrote, or `undefined` if the action never passed through the stamp branch. */
const readStamp = (action: AnyFSA | undefined): { seq: number; laneKey: string; scopeId: string } | undefined => {
    const seq = action?.meta?.[PAYMENT_SEQ_META];
    const laneKey = action?.meta?.[PAYMENT_SEQ_LANE_META];
    const scopeId = action?.meta?.[PAYMENT_SEQ_SCOPE_META];
    // All three are written in one object literal, so a seq missing either means something other than this
    // middleware fabricated it. Re-deriving the lane or the scope from live state here is exactly the drift
    // that stamping each one exists to prevent (see PAYMENT_SEQ_LANE_META / PAYMENT_SEQ_SCOPE_META), so such
    // an action is left alone rather than guessed at.
    return typeof seq === 'number' && typeof laneKey === 'string' && typeof scopeId === 'string'
        ? { seq, laneKey, scopeId }
        : undefined;
};

/**
 * An off-current-offer payment carries its target at `meta.middleware.offerType`, so it must not share a
 * lane with the visible offer type — otherwise a background Lease calculation could supersede a Finance edit
 * the user is watching.
 *
 * `||`, not `??`, for the FIRST fallback, because that is what offer-redux does when it decides which offer
 * type to BUILD the payload for (`getActionMetaValue(action, 'middleware')?.offerType || getCurrentOfferType(state)`
 * in `paymentActionCreators`). With `??` an empty-string `offerType` would land the action in a `'|main'`
 * lane that no other action ever uses, while the request itself went out for the current offer type — the
 * guard would silently be a no-op for that call.
 *
 * The trailing `|| ''` is a SEPARATE, narrower fallback: `getCurrentOfferType` reads `state.offer.offerType`,
 * which can be unset before the offer slice hydrates. It lands in the same `'|main'` lane as the case above —
 * that is accepted, not avoided, and `'falls back to an empty offer type when none can be resolved'` in the
 * spec pins the behavior — because a payment dispatched that early has no real offer type to key a lane on
 * either way, and every such payment collapses into the one sentinel lane together rather than each silently
 * getting its own unguarded lane.
 */
// `action.meta.middleware` is non-optional here: the only caller is the stamp branch, which reached it by
// reading `meta.middleware.payment` as truthy.
const resolveOfferType = (action: AnyFSA, state: StateTree): string =>
    action.meta.middleware.offerType || offerReduxSelectors.getCurrentOfferType(state) || '';

const isRegularGridRowTrigger = (triggerAction: AnyFSA): boolean => {
    const paymentGrid = triggerAction.meta.paymentGrid;
    return Boolean(paymentGrid?.gridAction) && !paymentGrid.isFlexBuyPayment;
};

/**
 * Lane key. `${offerType}|main` and nothing more.
 *
 * Because non-main grid rows are excluded wholesale by `isGuardable`, the guard never needs per-row lane
 * identity — and so has no cash-down-derived key that could mismatch the moment the user edits a cash-down
 * value. That absence is the design, not an omission.
 */
const laneKeyFor = (action: AnyFSA, state: StateTree): string => `${resolveOfferType(action, state)}|main`;

/**
 * A follow-up keyed on action META rather than on the trigger's type, so no exempt-list entry can express it:
 * `missingRequiredPSFields` is written by offer-redux's payload builder (`doOverridesExistForOfferType`) onto
 * the action it is building a payload for — which is the copy this middleware stamped — and dash
 * `paymentMiddleware` keys `applyDealerOverrides()` off it in its `PAYMENT_RECEIVED` case for ANY trigger.
 * Discard that response and the dealer overrides are never applied, until some unrelated payment happens to
 * re-trigger them.
 *
 * The flag is written during payload build, i.e. AFTER the stamp and after the request confirmation, so a call
 * carrying it did raise `latestDispatched` on the way down and can still supersede older responses — it is a
 * real payment. It only becomes interesting on the way back, once the flag is there to be read.
 */
// `action.meta` is established by every caller before this runs: a trigger reaches here only once `readStamp`
// has found a sequence and a lane on it, and the superseding trigger is one this middleware stamped itself.
const carriesDealerOverrideFollowUp = (action: AnyFSA): boolean => Boolean(action.meta.missingRequiredPSFields);

/**
 * Why a superseded response was forwarded rather than discarded — `undefined` when it is discardable. Doubles
 * as the `reason` on `PaymentSequencePassThrough`, because a dashboard that counted only discards could not
 * tell "no race happened here" from "the race happened and the guard was structurally unable to act".
 */
type PassThroughReason = 'gridRow' | 'exemptTrigger' | 'dealerOverrideFollowUp' | 'pendingOperation';

/**
 * DE766084. The list's one conditional entry. `UPDATED_SHOPPER_CONTACT` is dispatched by every customer-card
 * apply, including a name- or phone-only edit, and only the zip-changing one is program-invalidating — so only
 * that one can have its own response superseded by the refetch it triggers. Exempting the action type outright
 * would un-guard the rest, letting a name edit's stale response land on top of a newer payment.
 *
 * Reads `action.meta` directly for the same reason `carriesDealerOverrideFollowUp` does: both callers
 * establish it first. Here, only an action this middleware stamped reaches the check, and stamping requires
 * `meta.middleware.payment`; in `paymentMiddleware` the F280192 hold tests `action.meta?.middleware?.payment`
 * before asking.
 */
const isExemptInstance = (action: AnyFSA): boolean =>
    action.type !== offerReduxActionTypes.UPDATED_SHOPPER_CONTACT || action.meta.shopperZipChanged === true;

/**
 * The exemption question, for every consumer of the list. Ask this rather than
 * `PAYMENT_SEQUENCE_EXEMPT_TRIGGERS.has(action.type)`: membership alone is not the answer once an entry is
 * conditional, and `paymentMiddleware`'s F280192 hold reads the same list for a different purpose. A raw
 * `.has` there would exempt every customer-card apply from the AC-11 hold, not just the zip-changing one.
 */
export const isPaymentSequenceExemptTrigger = (action: AnyFSA): boolean =>
    PAYMENT_SEQUENCE_EXEMPT_TRIGGERS.has(action.type) && isExemptInstance(action);

/**
 * The half of un-discardability that is fixed for the life of a request, so it can be recorded in the lane's
 * ledger the moment the request is confirmed — see `LaneState`.
 */
const notDiscardableByType = (action: AnyFSA): PassThroughReason | undefined => {
    if (!isMainPaymentTrigger(action)) return 'gridRow';
    if (isPaymentSequenceExemptTrigger(action)) return 'exemptTrigger';
    return undefined;
};

/**
 * DE784527: the rate and residual overrides are exempt (DE761542) so that the edit's OWN follow-up - the UPQ/PQ
 * endpoint switch it triggers - cannot strand it. That is a reason to survive an exempt superseder, not an
 * ordinary one. A guarded edit dispatched after the override (cash down, term, price) built its request from
 * state that already holds the override, so its response carries the new rate too; the older response only
 * carries the state the edit replaced. Forwarded, it put back the old cash down's term: Cash Down showed the new
 * amount while Amt. Financed was priced off the old one.
 *
 * So behind `DE784527_Enable_Rate_Override_Supersede_Guard` the exemption is lifted once an ordinary edit has gone
 * out after the override (`LaneState.latestOrdinaryDispatched`), and only for what a stale response can do harm
 * with: a priced response. A failure or an abort carries no term to put back, and the override's own failure is
 * load-bearing - `programInvalidationMiddleware` falls back to user-program-quotes on it (`pqOverrideRecovery.ts`)
 * - so those stay exempt. The one reactor keyed on the override's `PAYMENT_RECEIVED` is that same recovery's hold
 * release, which the discard runs itself (`guardResolution`).
 */
const RATE_OVERRIDE_TRIGGERS: ReadonlySet<string> = new Set<string>([
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE
]);

const OUTCOME_ONLY_TYPES: ReadonlySet<string> = new Set<string>([
    paymentActionTypes.PAYMENT_FAILURE,
    paymentActionTypes.PAYMENT_ABORTED
]);

/** Fixed for the life of the request, like `notDiscardableByType`, so the ledger can record it on confirmation. */
const isExemptRateOverride = (action: AnyFSA): boolean =>
    RATE_OVERRIDE_TRIGGERS.has(action.type) && notDiscardableByType(action) === 'exemptTrigger';

/** The toggle is read last, so only a rate override an ordinary edit has actually overtaken pays for it. */
const isRateOverrideOvertaken = (action: AnyFSA, seq: number, lane: LaneState, state: StateTree): boolean =>
    isExemptRateOverride(action) &&
    seq < lane.latestOrdinaryDispatched &&
    featureToggleSelectors.isRateOverrideSupersedeGuardEnabled(state);

/**
 * Reads the ORIGINATING action, so on the resolution side this must be called with `action.meta.originalAction`
 * — never with the resolving action, whose own `type` is `PAYMENT_RECEIVED` and whose own meta has no
 * `paymentGrid`.
 *
 * THE DEALER-OVERRIDE CLAUSE IS RELATIVE TO THE SUPERSEDING CALL, not absolute. `missingRequiredPSFields` is
 * not the one-shot follow-up its name suggests: `doOverridesExistForOfferType` sets it on EVERY payment whose
 * term has no valid program, which is a durable deal state (no lender program picked yet, manual-program
 * dealer), not an event. Treating it as an unconditional opt-out therefore turned the guard off for whole
 * sessions. What actually matters is whether the follow-up will still fire once the dust settles, and it will
 * whenever the call that superseded this one carries the same flag — same state, same payload builder, and its
 * response is the one that gets forwarded. Only when the superseder does NOT carry it (the program became valid
 * mid-flight) is this response the last chance to apply the overrides, and only then is it kept.
 *
 * The superseding call's payload was built synchronously during its own dispatch, so by the time any older
 * response can land, `lane.latestDispatchedAction.meta` is already final.
 *
 * A rate override whose exemption is lifted (DE784527) falls through to that clause like any guarded edit, so it is
 * never less protected than one.
 */
const notDiscardableReason = (
    action: AnyFSA,
    superseder: AnyFSA,
    isExemptionLifted: () => boolean
): PassThroughReason | undefined => {
    const byType = notDiscardableByType(action);
    if (byType && !(byType === 'exemptTrigger' && isExemptionLifted())) return byType;
    if (carriesDealerOverrideFollowUp(action) && !carriesDealerOverrideFollowUp(superseder)) return 'dealerOverrideFollowUp';
    return undefined;
};

/**
 * Term-level incentive/rebate DOLLAR fields ride along inside offer-redux's `mergeTerms`, which replaces a
 * term's ENTIRE object wholesale (`{...prevTerms.byId, ...newTerms.byId}`) with no correlation to which
 * request produced the response. Most of them have no protection anywhere in offer-redux — they are pure
 * passthroughs, never restored.
 *
 * `incentives` (the applied-incentive CATALOG — `conditionals`/`nonConditionals`, each carrying its own
 * `exclusions`) is included for a different reason than the dollar fields above it: it was originally left
 * out on the theory that `preserveTermIncentives` (`termsStateUtils.ts`) already restores it. That theory
 * doesn't hold for THIS race — `preserveTermIncentives` only runs when `incentivesRequested` is false (a
 * response that didn't ask for incentive data), which is orthogonal to whether the response is STALE. A
 * stale response can absolutely have requested incentives, in which case its own (stale) catalog rides
 * through untouched. That matters downstream: `dropExclusiveConditionalIncentives` reads
 * `term.incentives.conditionals[].exclusions` to prune `appliedIncentiveIds` post-merge — if the catalog is
 * stale while the ids are freshly restored, the stale exclusion rules can prune an id this guard just put
 * back, undoing part of the fix one reducer step later. Restoring `incentives` alongside the ids keeps the
 * pair that `dropExclusiveConditionalIncentives` (and `dropNonCompatibleCatalogIncentives`, which reads the
 * same catalog for `compatibleIncentiveIds`) reads together actually consistent with each other.
 *
 * `initialPaymentDiscount` is excluded on purpose: it is Ford FlexBuy term-identity material
 * (`constants/payment.ts`'s `FORDFLEXBUY` extension key), not a rebate/incentive dollar amount, so
 * overwriting it here would be reaching outside this guard's actual scope.
 *
 * The lane guard above cannot close this: it is deliberately LANE-scoped (`${offerType}|main`), not
 * TERM-scoped, so it does nothing when two responses race on the SAME term id via paths the lane guard
 * doesn't discard — chiefly a grid row (wholesale excluded, `isMainPaymentTrigger` false) landing for the
 * same down-payment cell as the main call.
 *
 * So this tracks its own thing, per term id: the highest `paymentSeq` whose incentive fields have been
 * trusted for that term. A response whose seq is lower than a term's recorded high-water-mark has already
 * been beaten by a later-dispatched request's response, so its incentive-bearing fields are overwritten
 * with whatever is already in state before the action is forwarded — every other field on the term
 * (rates, residual, payment, program) still flows through untouched. This mirrors, and generalizes past
 * SELECTED_TERM, the narrower DE760840 guard already living in offer-redux
 * (`preserveSelectedTermAppliedIncentives` in `termsStateUtils.ts`).
 *
 * NOT applied to every forwarding branch in `guardResolution` — only to `!isSuperseded` and to the
 * `passThroughReason === 'gridRow'` case. `exemptTrigger` / `dealerOverrideFollowUp` / `pendingOperation`
 * are forwarded specifically because THIS response must land regardless of staleness (`APPLIED_PROGRAM`'s
 * whole reason for being exempt is delivering incentives-from-payment); splicing those fields back out is
 * "partly discarded" wearing the clothes of "never discarded". Grid rows carry no such guarantee — they are
 * exempt from the LANE guard purely because they are deliberately concurrent, not because any one of them
 * is owed delivery of its own stale data — so they are the one passthrough reason this still runs for.
 *
 * NEVER DELETES A FIELD. If `currentTerm` doesn't have a field — an unfamiliar term id, or state that
 * hasn't caught up yet — the incoming (stale) response's own value for that field is left in place rather
 * than blanked. Restoring is a strict improvement only when there is a confirmed value to restore; guessing
 * "absent" wrongly is actively destructive (wipes a legitimately-carried field), where doing nothing is
 * merely a missed protection. This also softens two related risks documented on this file's own review:
 * term ids get RE-KEYED in flight (`offer-redux/utils/incentives.ts`'s `sharedParamsDiffer` docstring — e.g.
 * `lease_term_48` becomes `lease_term_48_downpayment_4500_01` once a response lands), so a mark recorded
 * under one key can silently fail to protect a later response keyed differently for the same logical term;
 * and the mark below is written from the RESOLVING action, before the reducer decides whether to apply it
 * (offer-redux's `termsStateUtils.ts` leaves terms untouched for `meta.isStalePaymentResponse`, `CASH`, and
 * parts of `PAYMENT_FAILURE`), so a response that writes nothing can still raise a mark. Both failure modes
 * degrade to "this pair silently isn't protected" rather than "a legitimate field gets erased" only because
 * nothing here deletes.
 *
 * Restricted to `PAYMENT_RECEIVED` for the same reason: `PAYMENT_FAILURE` / `PAYMENT_ABORTED` /
 * `PAYMENT_FETCH_COMPLETED` can carry a `terms` payload without offer-redux ever applying it, so marking
 * from them risks exactly the false high-water-mark above for no protective benefit — `PAYMENT_RECEIVED` is
 * the only resolving type whose data offer-redux's reducer actually merges into the term.
 *
 * Deliberately NOT folded into `LaneState`/`lanes`: a term id is written by main AND grid-row responses
 * alike, and grid rows are wholesale excluded from lane bookkeeping by design. Scoped by store scope only,
 * same as `lanes`, so two scoped stores cannot cross-contaminate.
 *
 * Only called from a branch that is actually about to `next()` the action — a discarded response never
 * reaches here, so its seq must never be recorded as trusted, or a later-landing (but older-requested)
 * passthrough response for the same term could be wrongly judged fresh against data that was never applied.
 */
const TERM_INCENTIVE_FIELDS: ReadonlyArray<keyof PaymentTerm> = [
    'appliedIncentiveIds',
    'appliedIncentives',
    'appliedIncentiveAmount',
    'capReductionRebate',
    'discount',
    'rebate',
    'rebateAppliedToUpfrontItems',
    // `capCostReduction` / `netCashDown` are NOT incentive fields by name — they're here because
    // `isPricedTotalCorroborated` (offer-redux `incentiveSelectors.ts`) cross-checks
    // `capCostReduction - netCashDown === appliedIncentiveAmount` before trusting the applied amount
    // enough to reconcile the itemized incentive rows against it. Restoring `appliedIncentiveAmount`
    // alone and leaving these two on the stale response's values would let that cross-check compare
    // numbers from two different requests — passing or failing by coincidence rather than by the
    // invariant it exists to enforce. Lease-only in practice: offer-redux's own docs note finance
    // terms report no cap cost reduction, so this pair is a no-op there.
    'capCostReduction',
    'netCashDown',
    // See the doc block above: the applied-incentive catalog (conditionals/nonConditionals and their
    // exclusions), kept consistent with appliedIncentiveIds for dropExclusiveConditionalIncentives /
    // dropNonCompatibleCatalogIncentives, which read catalog and ids together.
    'incentives'
];

/**
 * DE785100. With the grid on, an edit prices every grid row with its own call, and a non-main row is never
 * discarded by the lane guard (`notDiscardableByType`). So when a row's older call lands after its newer one,
 * the older term overwrote the whole cell: monthly payment, amount financed, everything. Selecting that cell
 * serves the stored quote with no payment call, so the stale term became the deal's.
 *
 * Every forwarded `PAYMENT_RECEIVED` - main or grid row - records, per term id, the highest seq applied. A
 * grid-row response whose seq is older than that keeps the term already in state, whole. Main responses are
 * never patched here: the lane guard already orders them. Split halves of a row price different terms, so they
 * never mark each other.
 *
 * Unlike `guardStaleTermIncentiveWrites` this does not depend on DE767001, which is off in QA and prod.
 */
const guardStaleGridRowTerms = (
    state: StateTree,
    action: AnyFSA,
    seq: number,
    appliedTermSeq: Map<string, number>,
    isGridRow: boolean
): AnyFSA => {
    if (action.type !== paymentActionTypes.PAYMENT_RECEIVED || !featureToggleSelectors.isGridRowStaleTermGuardEnabled(state)) {
        return action;
    }
    const byId = (action.payload as { terms?: { byId?: Record<string, PaymentTerm> } } | undefined)?.terms?.byId;
    if (!byId) {
        return action;
    }

    let patchedById: Record<string, PaymentTerm> | undefined;
    for (const termId of Object.keys(byId)) {
        const highestSeq = appliedTermSeq.get(termId);
        if (highestSeq === undefined || seq >= highestSeq) {
            appliedTermSeq.set(termId, seq);
            continue;
        }
        // getTerm returns {} for a term id that is not in state (an in-flight re-keying, say). Keeping that would
        // blank a fully priced term, which is worse than the stale one, so the response's own term stays.
        const currentTerm = isGridRow ? offerReduxSelectors.getTerm(state, termId) : undefined;
        if (currentTerm && !isEmpty(currentTerm)) {
            patchedById = { ...(patchedById ?? byId), [termId]: currentTerm };
        }
    }
    if (!patchedById) {
        return action;
    }
    const payload = action.payload as { terms: Record<string, unknown> };
    return { ...action, payload: { ...payload, terms: { ...payload.terms, byId: patchedById } } };
};

const guardStaleTermIncentiveWrites = (
    state: StateTree,
    action: AnyFSA,
    seq: number,
    termIncentiveSeq: Map<string, number>,
    triggerType: string,
    context: UsabilityContext
): AnyFSA => {
    if (action.type !== paymentActionTypes.PAYMENT_RECEIVED) {
        return action;
    }
    // Independent kill switch from isPaymentSequenceGuardEnabled — see the selector's doc comment.
    // areFeatureTogglesAvailable is already confirmed by the caller before this function is ever reached.
    if (!featureToggleSelectors.isTermIncentiveGuardEnabled(state)) {
        return action;
    }
    const byId = (action.payload as { terms?: { byId?: Record<string, PaymentTerm> } } | undefined)?.terms?.byId;
    if (!byId) {
        return action;
    }

    let patchedById: Record<string, PaymentTerm> | undefined;
    for (const termId of Object.keys(byId)) {
        const highestSeq = termIncentiveSeq.get(termId);
        if (highestSeq === undefined || seq >= highestSeq) {
            termIncentiveSeq.set(termId, seq);
            continue;
        }
        // seq < highestSeq: a later-dispatched request's response has already written this term's
        // incentive fields. Restore them from state rather than letting this older response win.
        // `getTerm` (not a hand-rolled `state.offer.terms.byId` read) because it already resolves the
        // dual-mode offer-node lookup (`getOfferNode`/`getStateSlice`) that real hosts and test harnesses
        // mounting offer-redux's rootReducer at the state root both depend on.
        const currentTerm = offerReduxSelectors.getTerm(state, termId);
        const staleTerm = byId[termId];
        const restored: PaymentTerm = { ...staleTerm };
        const restoredFields: string[] = [];
        for (const field of TERM_INCENTIVE_FIELDS) {
            if (field in currentTerm) {
                (restored as Record<string, unknown>)[field] = currentTerm[field];
                restoredFields.push(field);
            }
            // else: leave the stale response's own value for this field untouched — see the doc block
            // above for why deleting on an unconfirmed mark is the actively dangerous direction.
        }
        patchedById = { ...(patchedById ?? byId), [termId]: restored };
        // This is the rollout signal for this guard specifically (see TERM_INCENTIVE_RESTORED in
        // usabilityTelemetry.ts) — without it there is no way to tell, in production, whether this
        // fires once a week or on every payment, or whether the re-keying gap documented above makes
        // it a silent no-op for a given deal type. Emitted per term, not once per action, so a
        // multi-term response (a grid batch) reports each restored term individually.
        //
        // termCountInResponse (Object.keys(byId).length, this restore's OWN stale response) answers, in
        // production, the question the README's "term identity" assumption rests on: is a colliding
        // response a single-cell answer, or one term among many priced as a side effect of a broader
        // sweep (SELECTED_CREDIT_TIER's termStrategy: 'all')? A value > 1 here is worth a closer look.
        emitTelemetry(() =>
            trackTermIncentiveRestored(
                {
                    termId,
                    supersededSeq: seq,
                    highestSeq,
                    resolvedType: action.type,
                    triggerType,
                    restoredFields: restoredFields.join(','),
                    termCountInResponse: Object.keys(byId).length
                },
                context
            )
        );
    }

    if (!patchedById) {
        return action;
    }
    const payload = action.payload as { terms?: { byId?: Record<string, PaymentTerm> } };
    return {
        ...action,
        payload: {
            ...payload,
            terms: { ...payload.terms, byId: patchedById }
        }
    };
};

/**
 * Telemetry, wrapped so it can never change what the guard does.
 *
 * Both emit sites sit in front of a decision the caller depends on — one in front of `next(action)` on the
 * fail-open path, one in front of the synthetic `PAYMENT_ABORTED` that clears the spinner. An unguarded
 * throw from a context selector there would convert a stale-number case into the hung slideout or stuck
 * recalculating dialog this middleware exists to avoid, which is a strictly worse failure than losing an
 * event. `findPendingOperation` guards its selectors for the same reason — and, like that guard, this one
 * must not be SILENT about it: `PaymentSequenceDiscarded` / `PaymentSequencePassThrough` are the rollout
 * signal, and a swallowed throw makes a broken `buildUsabilityContext` selector read as "no races happened"
 * rather than "the event never made it out".
 */
const emitTelemetry = (emit: () => void): void => {
    try {
        emit();
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('paymentSequenceMiddleware: telemetry emit threw and was dropped.', error);
        }
    }
};

/**
 * ── Stamp ────────────────────────────────────────────────────────────────
 * Numbers a payment trigger into its lane and forwards a copy carrying the stamp. Neither the lane's bar nor
 * its in-flight ledger is touched here: whether this action produces a request at all is decided further
 * down the chain, so both happen on `PAYMENT_REQUESTED` instead — see the middleware docstring and
 * `LaneState`. Every payment is stamped regardless of discardability, which is what lets
 * `paymentRaceTelemetryMiddleware` measure row-level ordering for grid rows.
 */
const stampAction = (store: ReduxStore<StateTree>, next: ReduxNext, action: AnyFSA, state: StateTree) => {
    // Resolved once and reused for both the counter lookup and the meta stamp below, so the scope this
    // action's sequence is numbered into is exactly the scope pinned onto it — see PAYMENT_SEQ_SCOPE_META.
    const scopeId = resolveScopeId(store);
    const scopeState = getScopeState(store, scopeId);
    scopeState.counter += 1;
    const seq = scopeState.counter;
    const laneKey = laneKeyFor(action, state);

    // Forward a COPY, never mutate in place. This middleware runs upstream of paymentMiddleware, so
    // `withMeta` captures whatever object we forward as `originalAction` — mutating the caller's
    // action object would work but leaks a store-internal counter into a value the caller may reuse.
    return next({
        ...action,
        meta: {
            ...action.meta,
            [PAYMENT_SEQ_META]: seq,
            [PAYMENT_SEQ_LANE_META]: laneKey,
            [PAYMENT_SEQ_SCOPE_META]: scopeId
        }
    });
};

/**
 * ── Request confirmation ─────────────────────────────────────────────────
 * A request is now genuinely out, so this is where both halves of the lane's state move.
 *
 * The ledger counts EVERY payment, discardable or not: an exempt trigger and a grid row own the spinner
 * exactly as much as a field edit does. The bar is raised by every MAIN payment, exempt included —
 * exemption governs what may be discarded, never what supersedes (see PAYMENT_SEQUENCE_EXEMPT_TRIGGERS).
 * Grid rows are the one exclusion, because they are deliberately concurrent and a grid sweep would
 * otherwise discard the main edit that ran alongside it.
 */
const recordRequestConfirmation = (
    store: ReduxStore<StateTree>,
    next: ReduxNext,
    action: AnyFSA,
    originalAction: AnyFSA,
    seq: number,
    lane: LaneState,
    scopeState: ScopeSeqState
) => {
    lane.inFlight.set(seq, {
        discardable: !notDiscardableByType(originalAction),
        isMainPaymentTrigger: isMainPaymentTrigger(originalAction),
        isRateOverride: isExemptRateOverride(originalAction)
    });
    // `>` rather than a bare assignment: confirmations arrive in dispatch order, so this only ever guards
    // against a duplicate PAYMENT_REQUESTED for an older call walking the bar backwards.
    if (isMainPaymentTrigger(originalAction) && seq > lane.latestDispatched) {
        lane.latestDispatched = seq;
        lane.latestDispatchedAction = originalAction;
    }
    if (!notDiscardableByType(originalAction) && seq > lane.latestOrdinaryDispatched) {
        lane.latestOrdinaryDispatched = seq;
    }
    const totalInFlight = [...scopeState.lanes.values()].reduce(
        (sum, l) => sum + [...l.inFlight.values()].filter((e) => e.isMainPaymentTrigger).length,
        0
    );
    // Dispatch before next(): counter must be hot before PAYMENT_REQUESTED reaches reducers/selectors —
    // moving this after next() opens a window where a selector read sees count = 0 while a payment is in-flight.
    store.dispatch(offerActionCreators.updatePaymentInFlightCount(totalInFlight));
    return next(action);
};

/**
 * ── Guard ────────────────────────────────────────────────────────────────
 * Whether THIS resolving action's response may reach `next` — discard, forward-with-a-documented-reason, or
 * forward-and-recover the spinner — now that `lane.latestDispatched` reflects every request confirmed before
 * it. See `LaneState`, `PassThroughReason`, and the middleware docstring for the semantics this enforces.
 */
const guardResolution = (
    store: ReduxStore<StateTree>,
    next: ReduxNext,
    state: StateTree,
    action: AnyFSA,
    originalAction: AnyFSA,
    seq: number,
    laneKey: string,
    lane: LaneState,
    scopeState: ScopeSeqState
) => {
    const isTerminal = TERMINAL_TYPES.has(action.type);
    // Bookkeeping happens before every early return below, or a non-discardable payment would be added to
    // `inFlight` on its request and never removed on its response — a leak that suppresses the recovery
    // dispatch below for the life of the lane.
    const wasInFlight = isTerminal && lane.inFlight.delete(seq);
    const isSuperseded = seq < lane.latestDispatched;

    if (!isSuperseded) {
        // The latest-dispatched call in this lane has resolved and cleared the spinner itself, so older
        // DISCARDABLE entries are now irrelevant — their responses can only be discarded from here on.
        // Dropping them is also what keeps a skipped resolution (confirmed while the toggle was on, resolved
        // while it was off) from leaking an entry forever.
        //
        // Gated on the resolving call being a main payment, and run before the discardability check below, so
        // that neither a grid row resolving alongside (which never raised the bar and so says nothing about
        // the main call) nor a response that turned non-discardable on the way back can either sweep entries
        // it has no business sweeping or leave the lane un-swept.
        //
        // An overtaken rate override counts as discardable here (DE784527). Left in, it held the in-flight count up
        // after the newer call had finished, and its own discard then found an empty lane and fell through to the
        // synthetic PAYMENT_ABORTED net. Its failure would still be forwarded, but a forwarded response needs no
        // ledger entry to be correct.
        const sizeBeforeSweep = lane.inFlight.size;
        if (isTerminal && isMainPaymentTrigger(originalAction)) {
            lane.inFlight.forEach(
                (entry, outstanding) =>
                    outstanding < seq &&
                    (entry.discardable ||
                        (entry.isRateOverride &&
                            outstanding < lane.latestOrdinaryDispatched &&
                            featureToggleSelectors.isRateOverrideSupersedeGuardEnabled(state))) &&
                    lane.inFlight.delete(outstanding)
            );
        }
        // Dispatch AFTER the sweep so the count reflects the final ledger state. Gated on any ledger mutation
        // (wasInFlight covers delete(seq); size comparison covers sweep deletions) so a response that touched
        // nothing — e.g. a seq that was never confirmed — does not produce a spurious dispatch.
        if (wasInFlight || lane.inFlight.size < sizeBeforeSweep) {
            const totalInFlight = [...scopeState.lanes.values()].reduce(
                (sum, l) => sum + [...l.inFlight.values()].filter((e) => e.isMainPaymentTrigger).length,
                0
            );
            store.dispatch(offerActionCreators.updatePaymentInFlightCount(totalInFlight));
        }
        return next(
            guardStaleGridRowTerms(
                state,
                guardStaleTermIncentiveWrites(
                    state,
                    action,
                    seq,
                    scopeState.termIncentiveSeq,
                    originalAction.type,
                    buildUsabilityContext(state)
                ),
                seq,
                scopeState.appliedTermSeq,
                // A grid row never raises the lane bar, so it is never superseded and lands on this path.
                !isMainPaymentTrigger(originalAction)
            )
        );
    }

    // `latestDispatchedAction` is set with `latestDispatched` in one step, so being superseded is what
    // establishes that a superseding trigger was recorded.
    const passThroughReason = notDiscardableReason(
        originalAction,
        lane.latestDispatchedAction as AnyFSA,
        () => !OUTCOME_ONLY_TYPES.has(action.type) && isRateOverrideOvertaken(originalAction, seq, lane, state)
    );
    if (passThroughReason) {
        // Grid rows are excluded by design and resolve out of order constantly, so counting them would bury
        // the signal. Everything else is a response the guard would have discarded and could not — the number
        // a rollout needs in order to read a low discard count as "no race" rather than "no reach".
        if (isTerminal && passThroughReason !== 'gridRow') {
            emitTelemetry(() =>
                trackPaymentSequencePassThrough(
                    {
                        reason: passThroughReason,
                        supersededSeq: seq,
                        latestSeq: lane.latestDispatched,
                        resolvedType: action.type,
                        lane: laneKey,
                        triggerType: originalAction.type
                    },
                    buildUsabilityContext(state)
                )
            );
        }
        // Only 'gridRow' gets the term-incentive guard here. 'exemptTrigger' / 'dealerOverrideFollowUp'
        // are forwarded specifically because this response's data must land regardless of staleness —
        // splicing its incentive fields back out would defeat the exemption's entire purpose (see the
        // doc block above `guardStaleTermIncentiveWrites`).
        if (wasInFlight) {
            const totalInFlight = [...scopeState.lanes.values()].reduce(
                (sum, l) => sum + [...l.inFlight.values()].filter((e) => e.isMainPaymentTrigger).length,
                0
            );
            store.dispatch(offerActionCreators.updatePaymentInFlightCount(totalInFlight));
        }
        return next(
            guardStaleGridRowTerms(
                state,
                passThroughReason === 'gridRow'
                    ? guardStaleTermIncentiveWrites(
                          state,
                          action,
                          seq,
                          scopeState.termIncentiveSeq,
                          originalAction.type,
                          buildUsabilityContext(state)
                      )
                    : action,
                seq,
                scopeState.appliedTermSeq,
                passThroughReason === 'gridRow'
            )
        );
    }

    // Superseded. Last chance to bail out: a state-keyed pending operation may still be waiting on this
    // response, and hanging it is worse than rendering a stale number. Fail open, loudly.
    const pendingOperation = findPendingOperation(state);
    if (pendingOperation) {
        emitTelemetry(() =>
            trackPaymentSequencePassThrough(
                {
                    reason: 'pendingOperation',
                    selectorName: pendingOperation,
                    supersededSeq: seq,
                    latestSeq: lane.latestDispatched,
                    resolvedType: action.type,
                    lane: laneKey,
                    triggerType: originalAction.type
                },
                buildUsabilityContext(state)
            )
        );

        // Forward, and return what the rest of the chain returned. This is a forwarding branch, so it owes
        // its caller the same value every other forwarding branch returns — a bare `return undefined` here
        // would make `dispatch(paymentReceived)` evaluate to undefined on this path alone.
        //
        // NOT run through guardStaleTermIncentiveWrites: a pending operation is waiting on THIS exact
        // response, so splicing its incentive fields would hand that operation data it never asked for —
        // the same "partly discarded" problem as the exempt-trigger case above.
        const result = next(action);

        if (wasInFlight) {
            const totalInFlight = [...scopeState.lanes.values()].reduce(
                (sum, l) => sum + [...l.inFlight.values()].filter((e) => e.isMainPaymentTrigger).length,
                0
            );
            store.dispatch(offerActionCreators.updatePaymentInFlightCount(totalInFlight));
        }

        // Loud, but never load-bearing: the New Relic event above is the signal that matters, and this
        // middleware must not be able to break a dispatch it has already forwarded. Notably NOT a throw —
        // throwing after `next()` leaves the reducers applied and the exception escaping `store.dispatch`,
        // which turns any consumer test that happens to cross this path into a cryptic half-applied failure.
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error(
                `paymentSequenceMiddleware: forwarded a superseded ${action.type} (seq ${seq} < ${lane.latestDispatched}) ` +
                    `because pending operation "${pendingOperation}" still needs it. Stale values may render. ` +
                    `If "${originalAction.type}" owns a workflow whose completion depends on this response, ` +
                    `add it to PAYMENT_SEQUENCE_EXEMPT_TRIGGERS instead of relying on this fallback.`
            );
        }
        return result;
    }

    // Discard: do NOT call next. Every reactor downstream — and every reducer — is now unreachable for
    // this response.
    //
    // Everything below is ONCE PER STALE CALL, not once per action, which is why it is gated on the
    // terminal types. With `useFetchBasedSpinnerMV` on, offer-redux dispatches PAYMENT_FETCH_COMPLETED
    // *before* the terminal action for the same call, so an ungated block would run twice: two
    // `PaymentSequenceDiscarded` events (doubling the rollout metric this is the signal for) and, worse, two
    // synthetic PAYMENT_ABORTEDs — the first off the non-terminal action, which does no in-flight
    // bookkeeping, and so could land after a newer call had already succeeded.
    if (!isTerminal) {
        return undefined;
    }

    // A PQ override recovery releases its held fallback values on its own PAYMENT_RECEIVED, and the call that
    // superseded it carries no recovery of its own, so a discard that skipped the release would leave values held
    // for an edit that is long settled (DE784527: an overtaken rate override is the case that now gets here).
    if (action.type === paymentActionTypes.PAYMENT_RECEIVED && isPqOverrideRecoveryOutcome(action)) {
        releasePqOverrideRecoveryHold(store, state, action, offerReduxSelectors.getCurrentOfferType(state));
    }

    // This event is the rollout signal for the guard itself. `OutOfOrderPaymentResolved` (from
    // paymentRaceTelemetryMiddleware, deliberately registered UPSTREAM of this middleware) keeps measuring
    // how often the underlying race happens; this one measures how often the guard acted on it. Reading the
    // race counter alone cannot distinguish a working guard from one comparing the wrong lane, because both
    // would drive it to zero.
    emitTelemetry(() =>
        trackPaymentSequenceDiscarded(
            {
                supersededSeq: seq,
                latestSeq: lane.latestDispatched,
                resolvedType: action.type,
                lane: laneKey,
                triggerType: originalAction.type
            },
            buildUsabilityContext(state)
        )
    );

    if (wasInFlight) {
        const totalInFlight = [...scopeState.lanes.values()].reduce(
            (sum, l) => sum + [...l.inFlight.values()].filter((e) => e.isMainPaymentTrigger).length,
            0
        );
        store.dispatch(offerActionCreators.updatePaymentInFlightCount(totalInFlight));
    }
    if (wasInFlight && lane.inFlight.size === 0) {
        // This response was the last confirmed request in its lane, so nobody else is going to clear the
        // spinner. PAYMENT_ABORTED clears isCalculating/isFetching (offer-redux paymentReducer), and
        // paymentCallPending gates the footer's recalculating dialog — omitting either trades a stale-data bug
        // for a stuck-dialog bug.
        //
        // `wasInFlight` is what makes this fire at most once. A duplicate stale response, or one whose entry
        // was already dropped because the latest-dispatched call resolved and cleared the spinner on its own,
        // deletes nothing and so recovers nothing — where a bare size check would fire a redundant round
        // trip and two reducer writes for every older response still to land.
        //
        // Now that the bar is raised only by a CONFIRMED request, this is a net rather than a routine path:
        // every discard has a live confirmed successor whose own response will clear the spinner, so an empty
        // ledger here means the ledger itself is anomalous — a resolution missed while the toggle was off, or a
        // request confirmed twice. It is kept because the failure it prevents (isCalculating and
        // paymentCallPending stranded true) is unrecoverable without a reload, and costs two reducer writes.
        store.dispatch({
            type: paymentActionTypes.PAYMENT_ABORTED,
            meta: {
                originalAction,
                supersededSeq: seq,
                latestSeq: lane.latestDispatched,
                [PAYMENT_SEQ_DISCARDED_META]: true
            }
        });
        store.dispatch(offerActionCreators.updatePaymentCallPendingState(false));
    } else if (isRegularGridRowTrigger(originalAction)) {
        store.dispatch({ type: paymentActionTypes.PAYMENT_GRID_ROW_SUPERSEDED, meta: { originalAction } });
    }
    // else: a newer call is still outstanding and owns the spinner — discard silently.

    return undefined;
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    // Cheap discrimination FIRST. This middleware sits near the top of the chain, so it sees every action
    // the app dispatches; reading a feature toggle up here would put a toggleUseRegistry write and a
    // featureTogglePreconditionCheck on the hottest path in the app for the ~all of traffic that is not a
    // payment. Two field reads rule that traffic out before any selector runs.
    const isStamp = Boolean(action.meta?.middleware?.payment);
    // The confirmation that a request is actually going out: offer-redux's paymentMiddleware dispatches this
    // (through `withMeta`, hence `originalAction`) immediately before the fetch. `PAYMENT_REQUESTED` is also
    // a payment TRIGGER in its own right — `getPayment()` returns one carrying `meta.middleware.payment` —
    // and `withMeta` does not copy `meta.middleware`, so the two are told apart by `originalAction` alone.
    // The stamp branch runs first, so the trigger variant is stamped and the confirmation it causes is
    // counted, in that order.
    const isRequestConfirmation = action.type === paymentActionTypes.PAYMENT_REQUESTED && Boolean(action.meta?.originalAction);
    // Our own post-discard PAYMENT_ABORTED is excluded here — forward it, or it discards itself forever.
    const isResolving = RESOLVING_TYPES.has(action.type) && !action.meta?.[PAYMENT_SEQ_DISCARDED_META];
    if (!isStamp && !isRequestConfirmation && !isResolving) {
        return next(action);
    }

    const state = store.getState();

    // areFeatureTogglesAvailable BEFORE the toggle selector: featureTogglePreconditionCheck throws
    // pre-bootstrap and payment actions can dispatch before the toggle slice is hydrated. Same ordering
    // as leaseEndpointMiddleware.
    if (!featureToggleSelectors.areFeatureTogglesAvailable(state) || !featureToggleSelectors.isPaymentSequenceGuardEnabled(state)) {
        return next(action);
    }

    if (isStamp) {
        return stampAction(store, next, action, state);
    }

    // No stamp means the trigger never passed through the stamp branch (toggle flipped mid-flight, a payment
    // dispatched outside the meta.middleware.payment channel). Preserve those flows exactly.
    const stamp = readStamp(action.meta?.originalAction as AnyFSA | undefined);
    if (!stamp) {
        return next(action);
    }

    // Finding a stamp is what establishes that the trigger exists and carries a `meta`, which is why everything
    // below can read it without re-checking.
    const originalAction = action.meta.originalAction as AnyFSA;
    const { seq, laneKey, scopeId } = stamp;
    // `scopeId` is the one pinned at stamp time, not whatever `store` reports right now — see
    // PAYMENT_SEQ_SCOPE_META. `store` still supplies `__realStore`, which is safe to re-derive live because
    // it identifies the app's one root store instance rather than a scope that can change mid-flight.
    const scopeState = getScopeState(store, scopeId);
    const lane = getLane(scopeState, laneKey);

    return isRequestConfirmation
        ? recordRequestConfirmation(store, next, action, originalAction, seq, lane, scopeState)
        : guardResolution(store, next, state, action, originalAction, seq, laneKey, lane, scopeState);
};
